import React from 'react';
import Input from 'components/atoms/Input';
import Button from 'components/atoms/Button';
// import Divider from 'components/atoms/Divider';
// import GoogleButton from 'components/molecules/GoogleButton';
import ErrorMessage from 'components/molecules/ErrorMessage';
import { useForm } from 'hooks/useForm';
import { SignUpFormProps } from 'types/organisms.types';
import { SignUpFormData } from 'types/auth.types';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  onSignUp,
  // onGoogleSignUp,
  onSignIn,
  loading = false,
  error = ''
}) => {
  const {
    values,
    errors,
    setValue,
    handleSubmit,
    isSubmitting
  } = useForm<SignUpFormData>(
    {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    },
    (values) => {
      const newErrors: FormErrors = {};
      
      if (!values.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      
      if (!values.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      
      if (!values.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      
      if (!values.password.trim()) {
        newErrors.password = 'Password is required';
      } else if (values.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      
      if (!values.confirmPassword.trim()) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (values.password !== values.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      
      return newErrors;
    }
  );

  const [showError, setShowError] = React.useState<boolean>(true);

  React.useEffect(() => {
    setShowError(!!error);
  }, [error]);

  const onSubmit = async (formValues: SignUpFormData): Promise<void> => {
    if (onSignUp) {
      await onSignUp(formValues);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setShowError(true);
    handleSubmit(onSubmit);
  };

  // const handleGoogleClick = (): void => {
  //   setShowError(true);
  //   if (onGoogleSignUp) {
  //     onGoogleSignUp();
  //   }
  // };

  const handleSignInClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    if (onSignIn) {
      onSignIn();
    }
  };

  const dismissError = (): void => {
    setShowError(false);
  };

  const isFormDisabled = loading || isSubmitting;

  return (
    <div className="w-full max-w-md space-y-6">
      {error && showError && (
        <ErrorMessage 
          error={error}
          onDismiss={dismissError}
          className="mb-4"
        />
      )}

      {/* <GoogleButton
        onClick={handleGoogleClick}
        disabled={isFormDisabled}
      >
        Continue with Google
      </GoogleButton> */}

      {/* <Divider>or</Divider> */}

      <form onSubmit={handleFormSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="text"
            placeholder="First Name"
            value={values.firstName}
            onChange={(e) => setValue('firstName', e.target.value)}
            error={errors.firstName}
            disabled={isFormDisabled}
            required
            autoComplete="given-name"
          />
          <Input
            type="text"
            placeholder="Last Name"
            value={values.lastName}
            onChange={(e) => setValue('lastName', e.target.value)}
            error={errors.lastName}
            disabled={isFormDisabled}
            required
            autoComplete="family-name"
          />
        </div>

        <Input
          type="email"
          placeholder="Email Address"
          value={values.email}
          onChange={(e) => setValue('email', e.target.value)}
          error={errors.email}
          disabled={isFormDisabled}
          required
          autoComplete="email"
        />

        <Input
          type="password"
          placeholder="Create Password"
          value={values.password}
          onChange={(e) => setValue('password', e.target.value)}
          error={errors.password}
          disabled={isFormDisabled}
          required
          autoComplete="new-password"
        />

        <Input
          type="password"
          placeholder="Confirm Password"
          value={values.confirmPassword}
          onChange={(e) => setValue('confirmPassword', e.target.value)}
          error={errors.confirmPassword}
          disabled={isFormDisabled}
          required
          autoComplete="new-password"
        />

        <Button
          type="submit"
          variant="teal"
          className="w-full py-3 font-semibold"
          loading={isFormDisabled}
          disabled={isFormDisabled}
        >
          {isFormDisabled ? 'Creating account...' : 'Sign up'}
        </Button>
      </form>

      <div className="text-center text-sm text-gray-600">
        Already have an account?{' '}
        <button
          type="button"
          onClick={handleSignInClick} 
          className="font-medium text-teal-600 hover:text-teal-700 transition-colors duration-200"
          disabled={isFormDisabled}
        >
          Sign in
        </button>
      </div>
    </div>
  );
};

export default SignUpForm;
